/**
 * PrivacyScreen — política de privacidad de AetherLite.
 *
 * Se monta dentro del shell o standalone (sin login) según el flag. Cubre web
 * + apps móviles: Google OAuth, magic link del cliente, datos que sube el
 * cliente (peso/medidas/fotos), permisos de cámara/galería/notificaciones,
 * push tokens (Expo), generación con IA (Anthropic), almacenamiento PostgreSQL
 * en Hostinger, derechos GDPR/HabeasData.
 *
 * URL pública para Google Play / App Store: https://aetherlite.nilabs.co/privacy
 * (también accesible vía hash #/privacy desde la app).
 */
import { StandaloneHeader } from "./TermsScreen";

interface PrivacyScreenProps {
  standalone?: boolean;
}

export function PrivacyScreen({ standalone }: PrivacyScreenProps = {}) {
  return (
    <div className={"al-priv-root " + (standalone ? "al-priv-standalone" : "")}>
      {standalone && <StandaloneHeader title="Política de privacidad" />}

      <article className={standalone ? "al-priv-article-standalone" : "al-priv-article"}>
        {!standalone && <h1 className="al-priv-title">Política de privacidad</h1>}

        <p className="al-priv-lead">
          En Nilabs creemos que tus datos son tuyos. Esta política describe qué
          información recolectamos en AetherLite, para qué la usamos, dónde la
          guardamos y qué derechos tenés sobre ella.
        </p>

        <Section title="1. Información que recolectamos">
          <p>
            <strong>Datos de cuenta del entrenador:</strong> al iniciar sesión
            con Google recibimos tu email, nombre y foto de perfil pública
            (Google OAuth). No accedemos a tu correo, calendario, drive ni
            ninguna otra información de tu cuenta de Google.
          </p>
          <p>
            <strong>Datos del entrenador:</strong> el branding que configurás en
            "Mi marca" (tagline, redes sociales, colores, logo) se guarda
            asociado a tu cuenta para personalizar los planes que creás.
          </p>
          <p>
            <strong>Datos de tus clientes (cargados por el entrenador):</strong>{" "}
            los nombres, mediciones, objetivos y notas que cargás sobre tus
            clientes se almacenan como parte de los planes que generás.
          </p>
          <p>
            <strong>Datos del cliente final (app móvil):</strong> el cliente
            puede entrar a la app móvil de AetherLite con un enlace mágico
            enviado a su email (sin contraseña). Una vez dentro, puede registrar
            voluntariamente: su peso y medidas corporales, fotos de progreso que
            sube desde su cámara o galería, y marcar qué entrenamientos completó.
            Estos datos quedan asociados a su ficha y visibles para su
            entrenador.
          </p>
          <p>
            <strong>Tokens de notificación push:</strong> si el cliente o
            entrenador habilitan las notificaciones, guardamos un identificador
            de dispositivo (Expo Push Token) para poder enviar avisos relevantes
            (ej. "tu cliente completó un entrenamiento"). No es información
            personal identificable por sí sola y se elimina al desinstalar la app.
          </p>
          <p>
            <strong>Datos de uso:</strong> registramos eventos básicos de uso
            (planes creados, links generados, vistas de planes públicos,
            generaciones con IA) para facturación, analítica agregada y
            prevención de abuso.
          </p>
        </Section>

        <Section title="2. Cómo usamos tu información">
          <p>
            Usamos tus datos exclusivamente para operar el servicio: autenticarte,
            mostrar tus planes, aplicar los límites de tu tier de pricing,
            facturar tu suscripción cuando aplique y comunicarnos con vos sobre
            cambios importantes del servicio.
          </p>
          <p>
            <strong>No vendemos tus datos ni los compartimos con terceros</strong>{" "}
            con fines publicitarios. Tampoco hacemos profiling automatizado para
            decisiones que produzcan efectos legales sobre vos.
          </p>
        </Section>

        <Section title="3. Dónde se almacenan tus datos">
          <p>
            La aplicación está hospedada en infraestructura de Hostinger
            (servidor con presencia en Latinoamérica) y la base de datos es
            PostgreSQL gestionada por el equipo de Nilabs. Las copias de
            respaldo se rotan automáticamente y se cifran en reposo.
          </p>
          <p>
            Las imágenes (logos, fotos del entrenador, fotos de progreso que
            sube el cliente desde la app) se sirven desde el mismo servidor con
            HTTPS forzado y encabezados de seguridad estándar. Las fotos del
            cliente solo son visibles para él mismo y para su entrenador
            asignado.
          </p>
        </Section>

        <Section title="4. Permisos de la app móvil">
          <p>
            Las apps móviles de AetherLite (cliente y entrenador) solicitan
            permisos solo cuando los necesitás y para un fin claro:
          </p>
          <p>
            <strong>Cámara y galería de fotos:</strong> únicamente para que el
            cliente pueda subir fotos de progreso. Solo accedemos a la foto que
            elegís explícitamente — nunca leemos tu galería completa.
          </p>
          <p>
            <strong>Notificaciones:</strong> para avisarte de actividad
            relevante (un entrenamiento completado, una foto nueva, recordatorios
            del plan). Podés desactivarlas desde la configuración de tu
            dispositivo en cualquier momento.
          </p>
        </Section>

        <Section title="5. Servicios de terceros">
          <p>
            <strong>Google OAuth:</strong> usamos Google Identity Services para
            el login del entrenador. La política de privacidad de Google aplica a
            la verificación de tu cuenta.
          </p>
          <p>
            <strong>Anthropic (Claude):</strong> la generación de planes con IA
            envía los datos del cliente (edad, peso, objetivo, lesiones,
            preferencias) a la API de Anthropic para producir el plan. Anthropic
            no entrena modelos con estos datos según su política para uso vía
            API. No enviamos nombres ni datos de contacto.
          </p>
          <p>
            <strong>Expo Push Notifications:</strong> los avisos push se entregan
            a través del servicio de Expo, que reenvía a Apple (APNs) y Google
            (FCM). Solo viaja el texto del aviso y el token del dispositivo.
          </p>
          <p>
            <strong>Google Fonts:</strong> en la web cargamos tipografías desde
            fonts.googleapis.com. Google puede registrar tu IP al servir las
            fuentes.
          </p>
          <p>
            No usamos analytics de Google ni Facebook Pixel. No tenemos píxeles
            de tracking de terceros ni vendemos datos con fines publicitarios.
          </p>
        </Section>

        <Section title="6. Tiempo de retención">
          <p>
            Mientras tu cuenta esté activa, conservamos tus datos
            indefinidamente para que puedas seguir trabajando. Si cancelás tu
            suscripción, mantenemos lectura por 30 días para que puedas
            descargar tus planes; pasado ese período, los datos se eliminan
            (excepto registros mínimos requeridos por ley fiscal o auditoría).
          </p>
          <p>
            Las fotos de progreso que sube el cliente se eliminan del servidor
            cuando él las borra desde la app, o cuando el entrenador elimina al
            cliente. Los enlaces mágicos de acceso vencen a los 15 minutos y son
            de un solo uso.
          </p>
          <p>
            Los logs de servidor que contienen IPs y user-agents se rotan
            automáticamente cada 90 días.
          </p>
        </Section>

        <Section title="7. Tus derechos (GDPR / Habeas Data)">
          <p>
            Tenés derecho a: acceder a tu información, rectificarla cuando esté
            desactualizada, suprimirla cuando ya no sea necesaria, oponerte a
            ciertos tratamientos, y solicitar la portabilidad de tus datos en
            formato estándar (JSON o CSV).
          </p>
          <p>
            Para ejercer cualquiera de estos derechos, escribinos a{" "}
            <a className="al-priv-link" href="mailto:nilton.alfonso@nilabs.co">
              nilton.alfonso@nilabs.co
            </a>{" "}
            indicando claramente qué necesitás. Te respondemos dentro de los 15
            días hábiles, conforme la Ley 1581 de 2012 (Habeas Data) y el
            Reglamento UE 2016/679 (GDPR).
          </p>
        </Section>

        <Section title="8. Datos de tus clientes">
          <p>
            Como entrenador, sos el responsable del tratamiento de los datos
            personales de tus clientes. AetherLite actúa como encargado: los
            procesa solo según tus instrucciones (al cargarlos en planes) y los
            elimina cuando vos eliminás el plan o el cliente.
          </p>
          <p>
            Te pedimos que obtengas el consentimiento explícito de tus clientes
            antes de cargar su información en la plataforma y que les informes
            cómo pueden ejercer sus derechos (lo cual canalizás vos hacia
            nosotros si fuera necesario).
          </p>
        </Section>

        <Section title="9. Cookies y almacenamiento local">
          <p>
            En la web usamos <code>localStorage</code> del navegador para guardar
            tu token de sesión JWT (válido 30 días) y preferencias de UI (tema,
            paleta). En las apps móviles, el token de sesión se guarda cifrado en
            el almacenamiento seguro del sistema operativo (Keychain en iOS,
            Keystore en Android). No usamos cookies de tracking ni de terceros.
          </p>
        </Section>

        <Section title="10. Cambios a esta política">
          <p>
            Si hacemos cambios sustanciales que afecten cómo tratamos tus
            datos, te avisamos por email con al menos 14 días de anticipación.
            La fecha de última actualización siempre figura al pie de esta
            página.
          </p>
        </Section>

        <Section title="11. Contacto">
          <p>
            Para cualquier consulta, reclamo o ejercicio de derechos, escribinos
            a{" "}
            <a className="al-priv-link" href="mailto:nilton.alfonso@nilabs.co">
              nilton.alfonso@nilabs.co
            </a>{" "}
            o por la web de Nilabs en{" "}
            <a
              className="al-priv-link"
              href="https://nilabs.co"
              target="_blank"
              rel="noopener noreferrer"
            >
              nilabs.co
            </a>
            .
          </p>
        </Section>

        <p className="al-priv-updated">
          Última actualización: Mayo 2026 · Responsable de tratamiento: Nilabs ·
          Contacto:{" "}
          <a className="al-priv-link" href="mailto:nilton.alfonso@nilabs.co">
            nilton.alfonso@nilabs.co
          </a>
        </p>
      </article>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="al-priv-section">
      <h2 className="al-priv-h2">{title}</h2>
      {children}
    </section>
  );
}
